import React from "react";
import MuiSnackbarContent from "@material-ui/core/SnackbarContent";
import Button from "../Button";
import CloseButton from "../CloseButton";
import withStyles from "../../styles/withStyles";

import CheckCircleOutline from "../../icons/CheckCircle";
import WarningOutline from "../../icons/Warning";
import ErrorOutline from "../../icons/Error";
import InfoOutline from "../../icons/Info";
import red from "../../styles/colors/red";
import chroma from "chroma-js";

function Content({
  children,
  classes,
  icon,
  onClickButton,
  onClickButtonLabel,
  onClose,
  variant,
  ...props
}) {
  if (!["info", "success", "warning", "danger"].includes(variant))
    variant = "info";
  let alertClasses = { root: classes[`${variant}Root`] };
  let buttonClasses = classes[`${variant}Button`];
  let iconComponent = variant => {
    if (variant === "success")
      return (
        <CheckCircleOutline
          className={`${classes.iconPadding} ${classes.successIcon}`}
        />
      );
    if (variant === "warning")
      return (
        <WarningOutline
          className={`${classes.iconPadding} ${classes.warningIcon}`}
        />
      );
    if (variant === "danger")
      return (
        <ErrorOutline
          className={`${classes.iconPadding} ${classes.errorIcon}`}
        />
      );
    return (
      <InfoOutline className={`${classes.iconPadding} ${classes.infoIcon}`} />
    );
  };

  let message = (
    <span className={classes.message}>
      {icon && iconComponent(variant)}
      {children}
    </span>
  );

  let action = (
    <span>
      {onClickButton && onClickButtonLabel !== "" && (
        <Button
          variant="text"
          className={buttonClasses}
          onClick={onClickButton}
        >
          {onClickButtonLabel}
        </Button>
      )}
      <CloseButton onClick={onClose} />
    </span>
  );

  return (
    <MuiSnackbarContent
      classes={alertClasses}
      message={message}
      action={action}
      {...props}
    />
  );
}

let defaultStyle = (theme, color) => {
  let coloredTheme = {
    fontSize: "16px",
    fontWeight: "500",
    flexGrow: "1",
    maxWidth: "none",
    margin: "12px",
    color: theme.palette.text.primary,
    textShadow: `0 0 0 ${theme.palette.text.primary}`,
    boxShadow: theme.shadows[2]
  };

  Object.assign(coloredTheme, {
    backgroundColor: chroma(color)
      .saturate(0.25)
      .css()
  });
  return coloredTheme;
};

let defaultButton = (theme, color) => {
  let coloredTheme = {
    fontSize: "14px",
    fontWeight: "500",
    marginRight: "12px",
    padding: "4px 12px",
    color: theme.palette.text.primary,
    textShadow: `0 0 0 ${theme.palette.text.primary}`
  };

  Object.assign(coloredTheme, {
    backgroundColor: chroma(color)
      .brighten(0.5)
      .css(),
    "&:hover": {
      backgroundColor: chroma(color)
        .brighten(0.7)
        .css()
    },
    "&:focus": {
      backgroundColor: chroma(color)
        .brighten(0.7)
        .css(),
      boxShadow: `0 0 0 0.16rem ${chroma(color).brighten(0.9)}`
    },
    "&:active, &.Mui-active": {
      color: theme.palette.text.primary,
      backgroundColor: chroma(color)
        .brighten(0.9)
        .css()
    }
  });
  return coloredTheme;
};

let darkenIcon = color =>
  chroma(color)
    .darken(1.5)
    .css();

let styles = theme => ({
  infoRoot: defaultStyle(theme, theme.palette.info.main),
  successRoot: defaultStyle(theme, theme.palette.success.main),
  warningRoot: defaultStyle(theme, theme.palette.warning.main),
  dangerRoot: defaultStyle(theme, red[400]),

  infoIcon: { fill: darkenIcon(theme.palette.info.main) },
  successIcon: { fill: darkenIcon(theme.palette.success.main) },
  warningIcon: { fill: darkenIcon(theme.palette.warning.main) },
  errorIcon: { fill: darkenIcon(red[400]) },

  infoButton: defaultButton(theme, theme.palette.info.main),
  successButton: defaultButton(theme, theme.palette.success.main),
  warningButton: defaultButton(theme, theme.palette.warning.main),
  dangerButton: defaultButton(theme, red[300]),

  message: {
    display: "flex",
    alignItems: "center"
  },
  iconPadding: {
    height: "20px",
    width: "20px",
    paddingRight: "8px"
  }
});

export default withStyles(styles)(Content);
